package com.trevorwiebe.timesheet.admin.equipment.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.mileage.domain.MileageRepository
import com.trevorwiebe.timesheet.mileage.domain.EquipmentRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class EquipmentViewModel(
    private val equipmentRepository: EquipmentRepository,
    private val mileageRepository: MileageRepository
) : DisposableViewModel() {

    private val _state = MutableStateFlow(EquipmentState())
    val state = _state.asStateFlow()

    init {
        loadEquipment()
    }

    fun onEvent(event: EquipmentEvent) {
        when (event) {
            is EquipmentEvent.OnAddEquipmentClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingEquipment = null) }
            }
            is EquipmentEvent.OnEditEquipmentClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingEquipment = event.equipment) }
            }
            is EquipmentEvent.OnDismissDialog -> {
                _state.update { it.copy(showAddEditDialog = false, editingEquipment = null) }
            }
            is EquipmentEvent.OnSaveEquipment -> {
                saveEquipment(event)
            }
            is EquipmentEvent.OnDeleteEquipment -> {
                deleteEquipment()
            }
            is EquipmentEvent.OnRestoreEquipment -> {
                restoreEquipment()
            }
            is EquipmentEvent.OnMakeActiveAfterRestore -> {
                makeActiveAfterRestore(event.makeActive)
            }
            is EquipmentEvent.OnFilterToggled -> {
                _state.update { state ->
                    val current = state.activeFilters
                    val updated = if (event.filter in current) current - event.filter else current + event.filter
                    state.copy(activeFilters = updated)
                }
            }
            is EquipmentEvent.OnSortChanged -> {
                _state.update { it.copy(activeSort = event.sort) }
            }
        }
    }

    private var serviceStatusLoaded = false

    private fun loadEquipment() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val flow = equipmentRepository.getEquipment()
            flow.collect { result ->
                if (result.error != null) {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                } else {
                    @Suppress("UNCHECKED_CAST")
                    val equipment = result.data as List<Equipment>
                    _state.update { it.copy(isLoading = false, equipment = equipment, error = null) }
                    if (!serviceStatusLoaded) {
                        serviceStatusLoaded = true
                        loadServiceStatus(equipment)
                    }
                }
            }
        }
    }

    private fun loadServiceStatus(allEquipment: List<Equipment>) {
        viewModelScope.launch {
            _state.update { it.copy(isLoadingServiceStatus = true) }

            val statusEntries = allEquipment.map { equipment ->
                async {
                    val lastEntryResult = mileageRepository.getLastMileageEntryForEquipment(equipment.id)
                    val lastServiceResult = mileageRepository.getLastServiceEntryForEquipment(equipment.id)

                    val lastEntry = lastEntryResult.data as? MileageEntry
                    val lastServiceEntry = lastServiceResult.data as? MileageEntry ?: return@async null

                    val lastMileage = lastEntry?.mileageValue ?: lastServiceEntry.mileageValue
                    val mileagesSinceService = maxOf(0L, lastMileage - lastServiceEntry.mileageValue)
                    equipment.id to mileagesSinceService
                }
            }.awaitAll().filterNotNull()

            _state.update {
                it.copy(
                    isLoadingServiceStatus = false,
                    serviceStatusMap = statusEntries.toMap()
                )
            }
        }
    }

    private fun saveEquipment(event: EquipmentEvent.OnSaveEquipment) {
        viewModelScope.launch {
            val editing = _state.value.editingEquipment
            if (editing == null) {
                equipmentRepository.addEquipment(
                    Equipment(
                        id = "",
                        unitNumber = event.unitNumber,
                        type = event.type,
                        active = event.active,
                        serviceInterval = event.serviceInterval,
                        serviceWarningMiles = event.serviceWarningMiles,
                        deleted = false
                    )
                )
            } else {
                equipmentRepository.updateEquipment(
                    editing.copy(
                        unitNumber = event.unitNumber,
                        type = event.type,
                        active = event.active,
                        serviceInterval = event.serviceInterval,
                        serviceWarningMiles = event.serviceWarningMiles
                    )
                )
            }
            _state.update { it.copy(showAddEditDialog = false, editingEquipment = null) }
        }
    }

    private fun deleteEquipment() {
        viewModelScope.launch {
            val editing = _state.value.editingEquipment ?: return@launch
            equipmentRepository.updateEquipment(editing.copy(deleted = true, active = false))
            _state.update { it.copy(showAddEditDialog = false, editingEquipment = null) }
        }
    }

    private fun restoreEquipment() {
        viewModelScope.launch {
            val editing = _state.value.editingEquipment ?: return@launch
            equipmentRepository.updateEquipment(editing.copy(deleted = false))
            _state.update { it.copy(showAddEditDialog = false, showMakeActiveDialog = true) }
        }
    }

    private fun makeActiveAfterRestore(makeActive: Boolean) {
        viewModelScope.launch {
            if (makeActive) {
                val editing = _state.value.editingEquipment ?: return@launch
                equipmentRepository.updateEquipment(editing.copy(deleted = false, active = true))
            }
            _state.update { it.copy(showMakeActiveDialog = false, editingEquipment = null) }
        }
    }
}
